import { useRef, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { firebaseAuth, onSnapshot } from "../firebase";
import {
  getUserData,
  getUserDataByUsername,
  getUserRef,
} from "../utils/FirebaseUtils";
import {
  accountActions,
  fetchSavedFormQuestions,
  fetchAudienceLists,
  fetchReadyForPayment,
  fetchAllUserFollowersData,
  loadTeamMemberData,
  loadInitialDataFilters,
  fetchOnboardingFlows,
  loadInitialInstagramAccounts,
  loadInitialFacebookPages,
} from "../redux/slices/accountSlice";
import { fetchCreatorEvents } from "../redux/slices/eventSlice";
import { fetchCurrUserWishList } from "../redux/slices/wishMarkSlice";
import { loadInitialCampaigns } from "../redux/slices/campaignSlice";
import { createSubscribeTriggerCampaign } from "../utils/campaignUtils";
import { useNavigate } from "./useNavigate";
import { NavigationId } from "../navigation/AppParamList";
import { fetchInitialConversations } from "../redux/slices/conversationSlice";
import { MixpanelContext } from "../context/AnalyticsService";
import { typedAPI } from "@markit/common.api";
import { useOnMount } from "./useOnMount";
import { useMultiAccountManager } from "./useMultiAccountManager";
import { USER_LOGGED_OUT } from "../redux/actionTypes";
import { notificationActions } from "../redux/slices/notificationSlice";
import useAsyncOnMount from "./useAsyncEffectOnMount";

// Helper function to retry a function with exponential backoff
const retryWithBackoff = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
  timeout: number = 15000
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const timeoutPromise = new Promise<never>((_resolve, reject) => {
        setTimeout(
          () => reject(new Error(`Function timed out after ${timeout}ms`)),
          timeout
        );
      });

      const result = await Promise.race([fn(), timeoutPromise]);
      return result;
    } catch (error: any) {
      lastError = error;

      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff: delay = baseDelay * 2^attempt
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

export const useInitialAppLoad = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const unsubscribeRef = useRef<(() => void) | void>();
  const { loadAdditionalAccountsFromStorage } = useMultiAccountManager();

  // Load additional accounts from localStorage
  useAsyncOnMount(async () => {
    await loadAdditionalAccountsFromStorage();
  });

  // handle all initial app loading
  useOnMount(() => {
    const unsubscribeAuth = firebaseAuth.onIdTokenChanged(async (user) => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }

      if (user) {
        const tokenResult = await user.getIdTokenResult();
        typedAPI.setAuthToken(tokenResult.token);
        const isAdmin = tokenResult.claims.admin === true;

        // Check for admin override of user ID
        const urlParams = new URLSearchParams(window.location.search);
        const overrideUsername = urlParams.get("username");
        let targetUid: string = user.uid;

        if (isAdmin && overrideUsername) {
          const existingUserData =
            await getUserDataByUsername(overrideUsername);
          if (existingUserData) {
            if (existingUserData.teamOwnerUid) {
              // Account for admin mode viewing a team member account
              dispatch(loadTeamMemberData(existingUserData));
              targetUid = existingUserData.teamOwnerUid;
            } else {
              // Normal admin mode viewing an account
              targetUid = existingUserData.uid;
            }
            typedAPI.setAdminTargetUid(targetUid);
            // We need to track this so that we know the original uid when viewing other accounts through admin mode
            dispatch(accountActions.setOriginalAccountAdmin(user.uid));
          }
        } else {
          typedAPI.setAdminTargetUid(null);
          const currentUserData = await getUserData(user.uid);
          if (currentUserData?.teamOwnerUid) {
            dispatch(loadTeamMemberData(currentUserData));
            targetUid = currentUserData.teamOwnerUid;
          }
        }

        // Clear isOriginalAccountAdmin if no longer viewing other account through admin mode
        if (!overrideUsername) {
          dispatch(accountActions.setOriginalAccountAdmin(""));
        }

        // Guards against re-running the full initial load sequence below on every snapshot fire
        let hasLoadedInitialData = false;
        // Tracks the last-seen Stripe status so readyForPayment (cached separately in redux,
        // normally only computed once below) gets recomputed whenever it actually changes
        let lastStripeStatusSignature: string | undefined;

        // Listens for the document on snapshot to ensure it's created on firebase (ie. for new account sign ups)
        const unsubscribe = onSnapshot(
          getUserRef(targetUid),
          async (doc) => {
            const userData = doc.data();
            if (userData) {
              // Always keep account state in sync with the latest doc, but only
              // run the (expensive) initial load sequence once per session.
              dispatch(accountActions.login(userData));

              // readyForPayment doesn't fall out of stripeAccountStatus for free - re-derive it
              // whenever status changes (e.g. a creator finishes connecting Stripe mid-session)
              const stripeStatusSignature = `${userData.stripeAccountStatus}|${userData.testStripeAccountStatus}`;
              if (
                lastStripeStatusSignature !== undefined &&
                lastStripeStatusSignature !== stripeStatusSignature
              ) {
                dispatch(fetchReadyForPayment(userData.uid));
              }
              lastStripeStatusSignature = stripeStatusSignature;

              if (hasLoadedInitialData) {
                return;
              }
              hasLoadedInitialData = true;

              const start = new Date().getTime();
              const loadFunctions = [
                {
                  name: "fetchAllUserFollowersData",
                  fn: async () =>
                    dispatch(fetchAllUserFollowersData(userData.uid)),
                },
                {
                  name: "fetchCreatorEvents",
                  fn: async () => dispatch(fetchCreatorEvents(userData.uid)),
                },
                {
                  name: "fetchCurrUserWishList",
                  fn: async () => dispatch(fetchCurrUserWishList(userData.uid)),
                },
                {
                  name: "fetchReadyForPayment",
                  fn: async () => dispatch(fetchReadyForPayment(userData.uid)),
                },
                {
                  name: "fetchSavedFormQuestions",
                  fn: async () =>
                    dispatch(fetchSavedFormQuestions(userData.uid)),
                },
                {
                  name: "fetchAudienceLists",
                  fn: async () => dispatch(fetchAudienceLists(userData.uid)),
                },
                {
                  name: "loadInitialCampaigns",
                  fn: async () => dispatch(loadInitialCampaigns(userData.uid)),
                },
                {
                  name: "fetchInitialConversations",
                  fn: async () =>
                    dispatch(fetchInitialConversations(userData.uid)),
                },
                {
                  name: "loadInitialDataFilters",
                  fn: async () =>
                    dispatch(loadInitialDataFilters(userData.uid)),
                },
                {
                  name: "fetchOnboardingFlows",
                  fn: async () => dispatch(fetchOnboardingFlows(userData.uid)),
                },
                {
                  name: "loadInitialInstagramAccounts",
                  fn: async () =>
                    dispatch(loadInitialInstagramAccounts(userData.uid)),
                },
                {
                  name: "loadInitialFacebookPages",
                  fn: async () =>
                    dispatch(loadInitialFacebookPages(userData.uid)),
                },
              ];

              // Initial attempt
              const results = await Promise.allSettled(
                loadFunctions.map(({ fn }) => fn())
              );

              // Collect failed functions for retry
              const failedFunctions: typeof loadFunctions = [];
              results.forEach((result, index) => {
                if (result.status === "rejected") {
                  failedFunctions.push(loadFunctions[index]);
                }
              });

              // Retry failed functions
              if (failedFunctions.length > 0) {
                const retryResults: {
                  name: string;
                  success: boolean;
                  error?: any;
                }[] = [];

                for (const { name, fn } of failedFunctions) {
                  try {
                    await retryWithBackoff(fn);
                    retryResults.push({ name, success: true });
                  } catch (error) {
                    retryResults.push({ name, success: false, error });
                  }
                }

                const successfulRetries = retryResults.filter((r) => r.success);
                const failedRetries = retryResults.filter((r) => !r.success);

                if (mixpanel) {
                  mixpanel.track("Failed Initial App Load Attempted Retries", {
                    distinct_id: userData.uid,
                    source_type: "webapp",
                    num_failed_functions: failedFunctions.length,
                    failed_functions: failedFunctions
                      .map((f) => f.name)
                      .join(", "),
                    num_failed_retries: failedRetries.length,
                    failed_retries: failedRetries.map((r) => r.name).join(", "),
                    num_successful_retries: successfulRetries.length,
                    successful_retries: successfulRetries
                      .map((r) => r.name)
                      .join(", "),
                    summary: `Retry summary: ${successfulRetries.length} succeeded, ${failedRetries.length} failed`,
                  });
                }
                // Comment out until we want to test this again
                // await API.monitor.triggerSlackAlert({
                //   title: "Failed Webapp Initial Load",
                //   message: `User: ${
                //     userData.username
                //   }, Failed functions: ${failedFunctions
                //     .map((f) => f.name)
                //     .join(", ")}.`,
                // });
              }

              // Handle the non-Redux function separately with retry
              try {
                await retryWithBackoff(() =>
                  createSubscribeTriggerCampaign(userData)
                );
              } catch (error) {
                console.error("Error creating subscribe campaign:", error);
              }

              dispatch(accountActions.setAppInitialized(true));
              const end = new Date().getTime();
              console.log(
                "Total fetch time (app initialization): " + (end - start) / 1000
              );
            }
          },
          (error: any) => {
            console.error(error);
          }
        );

        unsubscribeRef.current = unsubscribe;
      } else {
        typedAPI.setAuthToken(null);
      }
    });
    return () => {
      unsubscribeAuth();
    };
  });

  // Handle logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "logout" && e.newValue) {
        dispatch({ type: USER_LOGGED_OUT });
        // Clear auth token when user logs out in another tab
        typedAPI.setAuthToken(null);
        navigate(NavigationId.WELCOME);
      }
      // Handle account switching other tabs when you switch accounts on current tab
      if (e.key === "accountMismatch" && e.newValue) {
        dispatch(notificationActions.setAccountMismatchModal(true));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return undefined;
};
